// Works on iOS 13+:
// with relative path to public folder: assets/money.XXXXX.wav
// - Doesn't work with global uri capacitor://localhost/assets/money.wav
// Android API 32:
// - with local relative path assets/money.XXXX.wav (it is appended to "public/..." path for android assets)
// import { SoundEffect } from 'capacitor-sound-effect'

// This is fork of community plugin for capacitor v4
// Works with path relative to build folder e.g. public/sounds/money.mp3
// import { NativeAudio } from '@capgo/native-audio'

// Doesn't work on ios 13, crash App[65197:4969039] [shim] BKSProcessAssertion "ConnectionTerminationWatchdog" initialized with invalid pid and restarts
// On ios 14+ generate warning "originator doesn't have entitlement com.apple.runningboard.assertions.webkit AND originator doesn't have entitlement com.apple.multitasking.systemappassertions"
// works with both absolute local urls and global uri capacitor://...
import { Howl } from 'howler'
import { Capacitor } from '@capacitor/core'
import { Device } from '@capacitor/device'

import Settings from '/src/store/Settings'

const sfxes = new Map()

// Cache for platform detection - initialized lazily
let platformInfoCache = null

/**
 * Get platform information (platform name and OS version)
 * Results are cached after first call
 * @returns {Promise<{platform: string, osVersion: number}>}
 */
async function getPlatformInfo() {
  if (platformInfoCache !== null) {
    return platformInfoCache
  }

  try {
    const info = await Device.getInfo()
    platformInfoCache = {
      platform: info.platform,
      osVersion: parseFloat(info.osVersion) || 0
    }
  } catch (e) {
    // Fallback for web or if Device plugin fails
    platformInfoCache = {
      platform: Capacitor.getPlatform(),
      osVersion: 0
    }
  }

  return platformInfoCache
}

/**
 * Detect if we should use HTML5 Audio mode
 * - Android 16+ (API 36+): Use Web Audio API (html5: false) due to WebView issues
 * - Older Android & iOS: Use HTML5 Audio (html5: true) for better compatibility
 * - Web: Use Web Audio API (html5: false)
 */
async function shouldUseHtml5Audio() {
  const platformInfo = await getPlatformInfo()

  // Android 16+ has issues with HTML5 Audio in WebView
  if (platformInfo.platform === 'android' && platformInfo.osVersion >= 16) {
    return false
  }

  // For older Android and iOS, use HTML5 Audio for compatibility
  if (platformInfo.platform === 'android' || platformInfo.platform === 'ios') {
    return true
  }

  // Web: use Web Audio API
  return false
}

// const SoundService = {
//   async load (assetId, path) {
//     if (sfxes.has(assetId)) return
//
//     // remove leading slash
//     path = path.replace(/^\//, '')
//
//     const params = {
//       assetId,
//       assetPath: path,
//       isUrl: false
//     }
//
//     await NativeAudio.preload(params)
//
//     sfxes.set(assetId, path)
//   },
//
//   async loadAndPlay(id, src) {
//     if (!sfxes.has(id)) {
//       await SoundService.load(id, src)
//     }
//
//     await SoundService.play(id)
//   },
//
//   play(id) {
//     if (!Settings.getSound()) return
//
//     // console.log('SoundService: playing', id)
//     return NativeAudio.play({ assetId: id })
//   },
//
//   async remove(id) {
//     await NativeAudio.unload({ assetId: id })
//     sfxes.delete(id)
//   }
// }

// SoundEffects implementation
// const SoundService = {
//   load (id, path) {
//     const p = path.replace(/^\//, '')
//     return SoundEffect.loadSound({ id, path: p })
//       .then(() => sfxes.set(id, p))
//   },
//
//   play (id) {
//     if (!Settings.getSound()) return
//     return SoundEffect.play({ id })
//   },
//
//   async loadAndPlay(id, src) {
//     if (!sfxes.has(id)) {
//       await SoundService.load(id, src)
//     }
//
//     return SoundService.play(id)
//   },
//
//   remove (id) {
//     console.warn('SoundEffect unload not implemented', id)
//     // sfxes.delete(id)
//   }
// }

// Howler implementation
const SoundService = {
  async load (id, src, options = {}) {
    if (sfxes.has(id)) {
      return sfxes.get(id)
    }

    const useHtml5 = await shouldUseHtml5Audio()

    return new Promise((resolve, reject) => {
      // Determine format from file extension for better codec selection
      const format = src.split('.').pop()?.split('?')[0]?.toLowerCase()
      const formats = format ? [format] : undefined

      const defaultOptions = {
        src: [src],
        format: formats,
        preload: true,
        html5: useHtml5,
        onload: () => {
          // console.log('SoundService: loaded', id, src, { html5: useHtml5 })
          resolve(sfx)
        },
        onloaderror: (soundId, error) => {
          console.warn('SoundService: load error', { id, src, error, html5: useHtml5 })
          sfxes.delete(id)
          // Resolve instead of reject to prevent breaking the app
          // Sound simply won't play if it fails to load
          resolve(null)
        }
      }

      const sfx = new Howl(Object.assign(defaultOptions, options))
      sfxes.set(id, sfx)
    })
  },

  loadAndPlay (id, src) {
    // console.log('SoundService: loadAndPlay', id, src)
    if (sfxes.has(id)) {
      // console.log('SoundService: playing', id)
      return this.play(id)
    } else {
      // console.log('SoundService: loading', id)
      return this.load(id, src, { autoplay: true })
    }
  },

  play (id) {
    // TODO use howler configuration noAudio or mute instead - needs binding or event bus
    if (Settings.getSound()) {
      sfxes.get(id)?.play()
    }
  },

  remove (id) {
    if (sfxes.has(id)) {
      sfxes.get(id)?.unload()
      sfxes.delete(id)
    }
  }
}

export default SoundService
